import React, { useState, useEffect, useRef, useCallback } from 'react';
import { socialWallApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PostCard from '../components/SocialWall/PostCard';
import PostForm from '../components/SocialWall/PostForm';
import { Button } from '../components/ui/button';
import { Plus, Filter, Image, Smile } from 'react-feather';
import './SocialWall.css';

const SocialWall = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPostForm, setShowPostForm] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [postTypeFilter, setPostTypeFilter] = useState('all');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const lastPostElementRef = useCallback(node => {
    if (isLoadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMorePosts();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [isLoadingMore, hasMore]);

  const loadPosts = async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setIsLoadingMore(true);
      }

      const response = await socialWallApi.getAll(pageNum, 20, postTypeFilter);
      
      if (reset) {
        setPosts(response.posts || []);
      } else {
        setPosts(prev => [...prev, ...(response.posts || [])]);
      }
      
      setHasMore(response.hasMore);
      setPage(pageNum);
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Failed to load posts');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMorePosts = () => {
    if (!isLoadingMore && hasMore) {
      loadPosts(page + 1, false);
    }
  };

  useEffect(() => {
    loadPosts(1, true);
  }, [postTypeFilter]);

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
    setShowPostForm(false);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
  };

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  const handleLikeToggle = async (postId) => {
    try {
      const result = await socialWallApi.like(postId);
      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          return {
            ...p,
            is_liked: result.liked,
            likes_count: result.liked ? p.likes_count + 1 : p.likes_count - 1
          };
        }
        return p;
      }));
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleCommentAdded = (postId, comment) => {
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        return {
          ...p,
          comments: [...(p.comments || []), comment],
          comments_count: (p.comments_count || 0) + 1
        };
      }
      return p;
    }));
  };

  const handleCommentUpdated = (postId, commentId, updatedComment) => {
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        return {
          ...p,
          comments: p.comments.map(c => c.id === commentId ? updatedComment : c)
        };
      }
      return p;
    }));
  };

  const handleCommentDeleted = (postId, commentId) => {
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        return {
          ...p,
          comments: p.comments.filter(c => c.id !== commentId),
          comments_count: Math.max(0, (p.comments_count || 0) - 1)
        };
      }
      return p;
    }));
  };

  if (loading && posts.length === 0) {
    return (
      <div className="social-wall-container">
        <div className="social-wall-loading">Loading posts...</div>
      </div>
    );
  }

  return (
    <div className="social-wall-container">
      <div className="social-wall-header">
        <div className="social-wall-title">
          <span>Welcome to</span> <h1><strong>The Vibe</strong></h1>
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select 
            value={postTypeFilter} 
            onChange={(e) => setPostTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Posts</option>
            <option value="regular">Regular</option>
            <option value="birthday">Birthdays</option>
            <option value="celebration">Celebrations</option>
            <option value="announcement">Announcements</option>
          </select>
        </div>
      </div>

      {error && <div className="social-wall-error">{error}</div>}

      {/* Create Post Box - Facebook Style */}
      <div className="create-post-box">
        <div className="create-post-header">
          <div className="create-post-avatar">
            {user?.photoURL ? (
              <img src={user.photoURL} alt={user.displayName} />
            ) : (
              <div className="create-post-initials">
                {user?.displayName?.charAt(0) || 'U'}
              </div>
            )}
          </div>
          <button
            className="create-post-input"
            onClick={() => setShowPostForm(true)}
          >
            What's on your mind, {user?.displayName?.split(' ')[0] || 'there'}?
          </button>
        </div>
        <div className="create-post-actions">
          <button
            className="create-post-action-btn"
            onClick={() => setShowPostForm(true)}
          >
            <Image size={20} color="#45bd62" />
            <span>Photo/Video</span>
          </button>
        </div>
      </div>

      {showPostForm && (
        <PostForm
          onClose={() => setShowPostForm(false)}
          onPostCreated={handlePostCreated}
        />
      )}

      <div className="social-wall-posts">
        {posts.length === 0 ? (
          <div className="social-wall-empty">
            <p>No posts yet. Be the first to share something!</p>
          </div>
        ) : (
          posts.map((post, index) => (
            <div
              key={post.id}
              ref={index === posts.length - 1 ? lastPostElementRef : null}
            >
              <PostCard
                post={post}
                currentUserId={user?.id}
                onPostUpdated={handlePostUpdated}
                onPostDeleted={handlePostDeleted}
                onLikeToggle={handleLikeToggle}
                onCommentAdded={handleCommentAdded}
                onCommentUpdated={handleCommentUpdated}
                onCommentDeleted={handleCommentDeleted}
              />
            </div>
          ))
        )}
        {isLoadingMore && (
          <div className="social-wall-loading-more">Loading more posts...</div>
        )}
      </div>
    </div>
  );
};

export default SocialWall;

